// New JSX component
import React, { useEffect, useState } from "react";

import { DataGrid } from "@mui/x-data-grid";
import useSimpleDataProvider from "../../../providers/lvSimpleProvider";
import { CircularProgress, FormControl, InputLabel, LinearProgress, MenuItem, Select } from "@mui/material";
import { Button, useNotify } from "react-admin";
import { lvService } from "../../../services/LvService";

import DropdownSelect from "./DropdownSelect";

/**
 * `SectionList` renders a list of sections in a DataGrid.
 * It allows the user to select rows and calls onIdChanges with the new set of IDs when the selection changes.
 *
 * @component
 * @param {Object} props - The properties object.
 * @param {Object[]} props.sections - An array of section objects to be displayed in the DataGrid.
 *      Each section object should have the properties: `id`, `sectionName`, `maximumOperatingTemperature`, and `emergencyRatingConfig`.
 * @param {function} props.onIdChanges - A callback function that is invoked when the selection changes in the DataGrid.
 *      It receives an array of the IDs of the currently selected rows as its argument.
 */
const SectionList = ({ sections, onIdChanges }) => {
    const columns = [
        { field: "id", headerName: "ID", width: 70 },
        { field: "sectionName", headerName: "Section Name", width: 400 },
        { field: "maximumOperatingTemperature", headerName: "MOT", width: 70 },
        {
            field: "emergencyRatingConfig",
            headerName: "Emergency Rating Configs",
            width: 800,
            valueGetter: (params) => {
                if (params.row.emergencyRatingConfig == null || params.row.emergencyRatingConfig.size == 0) {
                    return "None Assigned";
                }
                // convert from set
                return Array.from(params.row.emergencyRatingConfig)
                    .map((c) => c.id)
                    .join(", ");
            },
        },
    ];

    const handleSelectionChange = (newSelection) => {
        onIdChanges(newSelection);
    };

    return (
        <div style={{ height: 600, width: "100%" }}>
            <h4>Sections</h4>
            <DataGrid
                className="section-list"
                rows={sections}
                columns={columns}
                pageSizeOptions={[50, 1000]}
                checkboxSelection
                onRowSelectionModelChange={handleSelectionChange}
            />
        </div>
    );
};

const EmergencyRatingConfigSelect = ({ configs, onChange }) => {
    return (
        <FormControl variant="outlined" style={{ minWidth: 400 }}>
            <InputLabel id="config-select-label">Select Emergency Rating Config</InputLabel>
            <Select
                labelId="config-select-label"
                onChange={(e) => {
                    onChange(e);
                }}
                label="Select Config"
            >
                {configs.map((config) => (
                    <MenuItem key={config.id} value={config.id}>
                        {config.id} - {config.title}
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );
};

export const EmergencyRatingConfigSection = () => {
    const provider = useSimpleDataProvider();
    const notify = useNotify();
    
    const [isLoading, setIsLoading] = useState(false);

    const [clients, setClients] = useState([]);
    const [selectedClient, setSelectedClient] = useState("");

    const [allLines, setAllLines] = useState([]);
    const [lines, setLines] = useState([]);
    const [selectedLine, setSelectedLine] = useState("");

    const [sections, setSections] = useState([]);

    const [emergencyRatingConfigs, setEmergencyRatingConfigs] = useState([]);
    const [emergencyRatingConfigSections, setEmergencyRatingConfigSections] = useState([]);

    const fetchData = async () => {
        setIsLoading(true);
        const clients = await provider.getAll("portal/clients", { sort: { clientName: 'ASC'} });

        setClients(clients);

        const configs = await provider.getAll("portal/emergency-rating-config");
        setEmergencyRatingConfigs(configs);

        let lines = await provider.getAll(`masterdata/lines`);
        setAllLines(lines);

        const configSections = await provider.getByUrl("portalApi", "emergency-rating-config/sections");
        setEmergencyRatingConfigSections(configSections);
        setIsLoading(false);
    };

    const fetchLines = async (clientId) => {
        const lines = allLines.filter((l) => l.clientId == clientId);

        setLines(lines);
        setSections([]);
    };

    const mapEmergencyRatingConfigsToSections = (sections, configSections) => {
      
            const sectionsDict = {};

            for (let section of sections) {
                sectionsDict[section.id] = section;
            }

            for (let configSection of configSections) {
                
                const section = sectionsDict[configSection.sectionId];

                if (!section.emergencyRatingConfig) {
                    section.emergencyRatingConfig = new Set();
                }
               
                section.emergencyRatingConfig.add(emergencyRatingConfigs.find(v => v.id == configSection.emergencyRatingConfigId));
            }

           
            return sections;
     
    };

    const fetchSections = async (lineId) => {
        setSections([]);
        setIsLoading(true);
        
        let sections = await provider.getMany("masterdata/sections", { lineId });
        sections = sections.filter((s) => s.lineId == lineId);

        let configSections = await lvService.getEmergencyRatingConfigSectionsByLineId(lineId);

        sections = mapEmergencyRatingConfigsToSections(sections, configSections);
        
        setSections(sections);
        
        setIsLoading(false);

    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleClientChange = (event) => {
        const clientId = event.target.value;
        setSelectedClient(clientId);
        fetchLines(clientId);
    };

    const handleLineChange = (event) => {
        setSelectedLine(event.target.value);
        fetchSections(event.target.value);
    };

    const [erConfig, setErConfig] = useState(null);

    const setSelectedEmergencyRatingConfig = (event) => {
        const configId = event.target.value;

        const config = emergencyRatingConfigs.find((c) => c.id == configId);

        setErConfig(config);
    };

    const [selectedSections, setSelectedSections] = useState([]);

    const onIdChanges = (ids) => {
        setSelectedSections(ids);
    };

    const assignEmergencyRatings = (event, type) => {
        if (!erConfig) {
            alert("You must first select an Emergency Rating Config");
            return;
        }

        if (selectedSections.length == 0) {
            alert("You must first select one or more sections");
            return;
        }

        for (let sectonId of selectedSections) {
            const section = sections.find((s) => s.id == sectonId);

            if (type === "add") {
                if (!section.emergencyRatingConfig) {
                    section.emergencyRatingConfig = new Set();
                }

                section.emergencyRatingConfig.add(erConfig);
            } else if (type === "remove") {
                if (section.emergencyRatingConfig) {
                    section.emergencyRatingConfig.delete(erConfig);
                    
                }
            }
        }

        let newSections = [...sections];
        setSections(newSections);
    };

    const saveAll = async () => {
        setIsLoading(true);
        
        const flatSections = sections.map((section) => {
            return {
                sectionId: section.id,
                emergencyRatingConfigIds: section.emergencyRatingConfig
                    ? Array.from(section.emergencyRatingConfig).map((c) => c.id)
                    : [],
            };
        });
        
        const result = await lvService.saveEmergencyRatingConfigSections(flatSections);

        if (result?.length >= flatSections?.length) {
            notify('Configurations Saved Successfully!', {type: 'success', autoHideDuration: 2000 });
        }

        setIsLoading(false);
    };

    return (
        <>
            <DropdownSelect
                items={clients.map((client) => ({ ...client, name: client.clientName }))}
                selectedItem={selectedClient}
                handleItemChange={handleClientChange}
                label="Select Client"
            />

            {lines.length > 0 && (
                <DropdownSelect
                    items={lines.map((line) => ({ ...line, name: line.lineName }))}
                    selectedItem={selectedLine}
                    handleItemChange={handleLineChange}
                    label="Select Line"
                />
            )}
            
            {isLoading && <div><CircularProgress /> Loading</div>}

            {sections.length > 0 && <SectionList sections={sections} onIdChanges={onIdChanges} />}

            {sections.length > 0 && emergencyRatingConfigs.length > 0 && (
                <>
                    <div style={{ marginTop: "6em" }}>
                        <EmergencyRatingConfigSelect
                            onChange={(e) => setSelectedEmergencyRatingConfig(e)}
                            configs={emergencyRatingConfigs}
                        />
                        <Button
                            style={{ margin: "8px" }}
                            variant="outlined"
                            size="medium"
                            onClick={(e) => assignEmergencyRatings(e, "add")}
                            label="Add"
                        />
                        <Button
                            style={{ margin: "8px" }}
                            variant="outlined"
                            size="medium"
                            onClick={(e) => assignEmergencyRatings(e, "remove")}
                            label="Remove"
                        />
                    </div>
                    <div>
                        <Button variant="contained" size="large" onClick={() => saveAll()} label="Save" />
                    </div>
                </>
            )}
        </>
    );
};
